import { useEffect, useRef } from 'react';
import { useNotification } from '../NotificationContext';
import { useSystemWebSocket } from '../SystemWebSocketContext';
import { isAuthenticated } from '../common';
import { createWebSocket, wsEndpoints, authAPI } from '../api';
import { requestNotificationPermission } from '../utils/browserNotifications';
import { handleSystemNotification } from '../utils/systemNotifications';
import { recordIncomingChatMessage, resetChatBlinkStore } from '../utils/chatUnreadStore';

const SystemWebSocket = () => {
  const { showNotification } = useNotification();
  const { notifyHandlers, setSocket } = useSystemWebSocket();
  const systemSocketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 10;
  const baseReconnectDelay = 3000; // 3 seconds

  useEffect(() => {
    isMountedRef.current = true;
    reconnectAttemptsRef.current = 0;
    requestNotificationPermission();

    const connectSystemWebSocket = async () => {
      // Check authentication before connecting
      const authenticated = await isAuthenticated();
      if (!authenticated || !isMountedRef.current) {
        return;
      }

      // Close existing connection if any
      if (systemSocketRef.current) {
        try {
          systemSocketRef.current.onerror = null;
          systemSocketRef.current.onopen = null;
          systemSocketRef.current.onclose = null;
          systemSocketRef.current.onmessage = null;
          if (systemSocketRef.current.readyState === WebSocket.OPEN || 
              systemSocketRef.current.readyState === WebSocket.CONNECTING) {
            systemSocketRef.current.close();
          }
        } catch (error) {
          // Ignore errors when closing
        }
        systemSocketRef.current = null;
      }

      try {
        const response = await authAPI.getToken();
        const token = response.data.token;

        if (!token || !isMountedRef.current) {
          return;
        }

        const socket = createWebSocket(wsEndpoints.system(), token);
        systemSocketRef.current = socket;
        setSocket(socket); // Register socket in context for sending messages

        socket.onerror = (error) => {
          // Логируем ошибку только если это не первая попытка подключения
          // или если уже было несколько неудачных попыток
          if (reconnectAttemptsRef.current > 0) {
            console.warn(`System WebSocket connection error (attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts}):`, error);
          }
          // Иначе ошибка будет обработана в onclose с переподключением
        };

        socket.onopen = () => {
          reconnectAttemptsRef.current = 0; // Reset reconnect attempts on successful connection
          // Notify subscribers that WebSocket is connected
          if (isMountedRef.current) {
            notifyHandlers('ws_connected', { type: 'ws_connected' });
          }
        };

        socket.onmessage = (event) => {
          if (!isMountedRef.current) return;

          try {
            const data = JSON.parse(event.data);

            if (data.type === 'new_chat_message' && data.data) {
              recordIncomingChatMessage(data.data);
            }

            // Notify all subscribers about this message
            notifyHandlers(data.type, data);

            handleSystemNotification(data, showNotification);

            // Handle ping/pong for keepalive
            if (data.type === 'ping') {
              if (socket.readyState === WebSocket.OPEN) {
                socket.send(JSON.stringify({ type: 'pong' }));
              }
            }

          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };

        socket.onclose = (event) => {
          if (systemSocketRef.current === socket) {
            systemSocketRef.current = null;
            setSocket(null);
          }
          if (!isMountedRef.current) return;
          
          // Don't reconnect if it was a normal closure or we're unmounting
          if (event.code === 1000 || !isMountedRef.current) {
            return;
          }

          // Check authentication before reconnecting
          const shouldReconnect = reconnectAttemptsRef.current < maxReconnectAttempts;
          
          if (shouldReconnect) {
            reconnectAttemptsRef.current += 1;
            // Exponential backoff: 3s, 6s, 12s, etc., max 30s
            const delay = Math.min(baseReconnectDelay * Math.pow(2, reconnectAttemptsRef.current - 1), 30000);
            
            // Логируем только если это не первая попытка переподключения
            if (reconnectAttemptsRef.current > 1) {
              console.info(`System WebSocket: Reconnecting in ${delay / 1000}s (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})...`);
            }
            
            reconnectTimeoutRef.current = setTimeout(async () => {
              if (isMountedRef.current) {
                const authenticated = await isAuthenticated();
                if (authenticated) {
                  connectSystemWebSocket();
                }
              }
            }, delay);
          } else {
            console.error('System WebSocket: Max reconnection attempts reached. Please refresh the page.');
          }
        };

      } catch (error) {
        // Логируем ошибку только если это не первая попытка или если уже было несколько неудачных попыток
        if (reconnectAttemptsRef.current > 0) {
          console.warn(`Error connecting to system WebSocket (attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts}):`, error);
        }
        // Retry connection on error
        if (isMountedRef.current && reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current += 1;
          const delay = Math.min(baseReconnectDelay * Math.pow(2, reconnectAttemptsRef.current - 1), 30000);
          reconnectTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              connectSystemWebSocket();
            }
          }, delay);
        } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          console.error('System WebSocket: Max reconnection attempts reached. Please refresh the page.');
        }
      }
    };

    // Check authentication periodically and reconnect if needed
    const checkAuthAndConnect = async () => {
      const authenticated = await isAuthenticated();
      if (authenticated) {
        // Only connect if not already connected or connecting
        if (!systemSocketRef.current || 
            systemSocketRef.current.readyState === WebSocket.CLOSED ||
            systemSocketRef.current.readyState === WebSocket.CLOSING) {
          connectSystemWebSocket();
        }
      } else {
        // Close connection if user is not authenticated
        if (systemSocketRef.current) {
          try {
            systemSocketRef.current.close();
          } catch (error) {
            // Ignore errors
          }
          systemSocketRef.current = null;
          setSocket(null);
        }
      }
    };

    // Initial connection
    checkAuthAndConnect();

    // Listen for login events to connect immediately after login
    const handleUserLoggedIn = () => {
      if (isMountedRef.current) {
        // Check authentication and connect immediately when user logs in
        checkAuthAndConnect();
      }
    };

    const handleUserLoggedOut = () => {
      resetChatBlinkStore();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (systemSocketRef.current) {
        try {
          systemSocketRef.current.onerror = null;
          systemSocketRef.current.onopen = null;
          systemSocketRef.current.onclose = null;
          systemSocketRef.current.onmessage = null;
          systemSocketRef.current.close();
        } catch (_) {
          // ignore
        }
        systemSocketRef.current = null;
      }
      setSocket(null);
      reconnectAttemptsRef.current = 0;
    };

    window.addEventListener('userLoggedIn', handleUserLoggedIn);
    window.addEventListener('userLoggedOut', handleUserLoggedOut);

    // Check authentication every 30 seconds to ensure connection stays alive
    const authCheckInterval = setInterval(() => {
      if (isMountedRef.current) {
        checkAuthAndConnect();
      }
    }, 30000);

    return () => {
      isMountedRef.current = false;
      window.removeEventListener('userLoggedIn', handleUserLoggedIn);
      window.removeEventListener('userLoggedOut', handleUserLoggedOut);
      clearInterval(authCheckInterval);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (systemSocketRef.current) {
        try {
          systemSocketRef.current.onerror = null;
          systemSocketRef.current.onopen = null;
          systemSocketRef.current.onclose = null;
          systemSocketRef.current.onmessage = null;
          if (systemSocketRef.current.readyState === WebSocket.OPEN || 
              systemSocketRef.current.readyState === WebSocket.CONNECTING) {
            systemSocketRef.current.close();
          }
        } catch (error) {
          // Ignore errors
        }
        systemSocketRef.current = null;
      }
      setSocket(null);
    };
  }, [showNotification, notifyHandlers, setSocket]);

  return null;
};

export default SystemWebSocket;

